import { combineEpics } from 'redux-observable';
import {
  Observable,
  debounceTime,
  distinctUntilChanged,
  filter,
  from,
  ignoreElements,
  map,
  mergeMap,
  pipe,
  retry,
  take,
  takeUntil,
  withLatestFrom,
} from 'rxjs';

import module from './index';

import activeEditor from 'domains/active_editor';

import { notNull } from 'utils/type_guards';

import type { Epic } from 'redux-observable';
import type { Action } from '@reduxjs/toolkit';
import type { RootState } from 'store/wos_store';

const { actions, utils } = module;

type MutationObserverResult = {
  mutation: MutationRecord;
  id: string;
  svg: SVGSVGElement;
};

function isTextNodeProduct() {
  return pipe(
    map(activeEditor.selectors.product),
    filter(notNull),
    distinctUntilChanged((prev, curr) => prev.id === curr.id),
    map((product) => product.nodes.filter(utils.isTextNode)),
    filter((nodes) => nodes.length !== 0)
  );
}

const loadGoogleFontsEpic: Epic<Action, Action, RootState> = (action$, state$) =>
  state$.pipe(
    isTextNodeProduct(),
    take(1),
    map(() => {
      const head = document.head || document.getElementsByTagName('head')[0];
      if (!head.querySelector('#googleFontImport')) {
        const style = document.createElement('style');
        const css = `@import url("https://fonts.googleapis.com/css?family=${utils.fontList
          .join('|')
          .split(' ')
          .join('+')}");`;
        style.setAttribute('type', 'text/css');
        style.setAttribute('id', 'googleFontImport');
        style.appendChild(document.createTextNode(css));

        head.appendChild(style);
      }
    }),
    // We don't need any action out of this, it's just a side effect
    ignoreElements()
  );

const initializeTextNodeEpic: Epic<Action, Action, RootState> = (_, state$) =>
  state$.pipe(
    isTextNodeProduct(),
    map((nodes) => nodes.map((n) => ({ id: n.uid }))),
    map((payload) => actions.initializeTextNodes(payload))
  );

/**
 * Sets up MutationObservers for each text node when they are
 * initialized, completing the observable for those when a new
 * set of text nodes is initialized
 */
const autofitTextNodesEpic: Epic<Action, Action, RootState> = (action$, state$) =>
  action$.pipe(
    filter(actions.initializeTextNodes.match),
    mergeMap(({ payload }) =>
      from(payload.map((node) => node.id)).pipe(
        map((id) => {
          const svg = document.querySelector<SVGSVGElement>(`#text_node--svg_${id}`);
          // Throw an error that retry can catch
          if (!svg) throw new Error('not found');

          return { id, svg };
        }),
        retry({ delay: 100 }),
        mergeMap(({ svg, id }) =>
          createTextNodeMutationObserver(svg, id).pipe(
            debounceTime(200),
            takeUntil(action$.pipe(filter(actions.initializeTextNodes.match)))
          )
        )
      )
    ),
    map(({ svg, id }) => {
      const adjustment = utils.getTextAutofitCorrection(svg);
      if (!adjustment) return null;

      return { id, adjustment };
    }),
    filter(notNull),
    withLatestFrom(state$),
    map(([adjustmentPayload, state]) => {
      const textNode = state.textNodes.entities[adjustmentPayload.id];
      return [adjustmentPayload, textNode?.autofit] as const;
    }),
    filter(([_, shouldAutofit]) => !!shouldAutofit),
    map(([adjustmentPayload, _]) => actions.autofitTextAdjustment(adjustmentPayload)),
    take(20) // until kinks are worked out, make sure we don't blow up
  );

function createTextNodeMutationObserver(svg: SVGSVGElement, id: string) {
  const observable = new Observable<MutationObserverResult>((subscriber) => {
    const mo = new MutationObserver((mutationList) => {
      for (const mutation of mutationList) {
        subscriber.next({ mutation, id, svg });
      }
    });

    mo.observe(svg, {
      attributes: true,
      attributeOldValue: true,
      childList: true,
      subtree: true,
      characterDataOldValue: true,
    });
  });

  return observable;
}

const combined = combineEpics(loadGoogleFontsEpic, initializeTextNodeEpic, autofitTextNodesEpic);
export default combined;
